import React from 'react';
import { Animated, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as Updates from 'expo-updates';
import { rideSessionStore } from '../services/rideSessionStore';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * «LO ARREGLASTE Y ME SIGUE SALIENDO IGUAL»
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * EL PROBLEMA, MEDIDO (1-oct-2026). El fundador probó un arreglo publicado la
 * noche anterior y seguía viendo el fallo. Comprobado contra el servidor de
 * Expo con las MISMAS cabeceras que manda su teléfono (runtime 1.0.17, canal
 * production, android): HTTP 200 y el update correcto esperándolo. O sea que
 * el arreglo estaba ahí y no llegaba a su pantalla.
 *
 * POR QUÉ. El binario va con `EXPO_UPDATES_LAUNCH_WAIT_MS = 0`
 * (android/app/src/main/AndroidManifest.xml:104): la app arranca con el paquete
 * que ya tiene, se baja el nuevo POR DETRÁS y lo deja listo para el siguiente
 * ARRANQUE EN FRÍO. Y en Android eso casi nunca pasa: salir con el botón de
 * inicio y volver desde las recientes NO reinicia el proceso — devuelve el
 * mismo, con el JavaScript viejo. La app se tiene que cerrar del todo, o que
 * Android la mate por memoria. Puede tardar días. Para un usuario normal,
 * nunca.
 *
 * Y `expo-updates` estaba instalado (56.0.21) pero NINGUNA línea de la app lo
 * usaba: no había forma de enterarse de que había algo esperando, ni de
 * aplicarlo. Cada arreglo que se publicaba quedaba en el limbo.
 *
 * QUÉ HACE ESTO. Cuando hay un paquete ya descargado y listo (`isUpdatePending`),
 * enseña una barra que se puede tocar para aplicarlo al momento. No descarga
 * nada —de eso se encarga el arranque—, solo ofrece el reinicio que ya hacía
 * falta.
 *
 * ── LO QUE NO HACE, A PROPÓSITO ──────────────────────────────────────────
 *
 *  · NO reinicia solo. `reloadAsync()` recarga la app entera: hacerlo sin
 *    avisar, mientras alguien escribe su placa o mira su ruta, es peor que el
 *    fallo que viene a arreglar.
 *  · NO aparece durante un viaje. Se consulta `rideSessionStore.read()`: con un
 *    viaje activo la barra no se enseña. Un chofer llevando a un pasajero no
 *    puede tener delante un botón que le reinicie la aplicación.
 *  · NO aparece en desarrollo (`Updates.isEnabled` es false ahí).
 */
export default function AvisoDeActualizacion() {
  // `useUpdates` es seguro aunque los updates estén apagados: devuelve todo en
  // false. Aun así se mira `isEnabled` antes de dibujar nada.
  const { isUpdatePending } = Updates.useUpdates();

  const [enViaje, setEnViaje] = React.useState(true); // pesimista hasta saberlo
  const [aplicando, setAplicando] = React.useState(false);
  const deslizar = React.useRef(new Animated.Value(-80)).current;

  // Se comprueba SOLO cuando hay algo pendiente: no tiene sentido leer el
  // almacenamiento en cada arranque por una barra que casi nunca sale.
  React.useEffect(() => {
    let vivo = true;
    if (!isUpdatePending) return;
    rideSessionStore
      .read()
      .then((s) => {
        if (vivo) setEnViaje(!!s?.activeRideId);
      })
      .catch(() => {
        // Si no se puede leer, se asume que SÍ hay viaje y no se molesta a
        // nadie. Perder una actualización pesa menos que interrumpir un viaje.
        if (vivo) setEnViaje(true);
      });
    return () => {
      vivo = false;
    };
  }, [isUpdatePending]);

  const seVe = Updates.isEnabled && isUpdatePending && !enViaje;

  React.useEffect(() => {
    Animated.timing(deslizar, {
      toValue: seVe ? 0 : -80,
      duration: 260,
      useNativeDriver: true,
    }).start();
  }, [seVe, deslizar]);

  if (!Updates.isEnabled || !isUpdatePending) return null;

  const aplicar = async () => {
    if (aplicando) return;
    setAplicando(true);
    try {
      await Updates.reloadAsync();
    } catch (e: any) {
      // Si no se pudo recargar, la barra se queda: el update sigue pendiente y
      // entrará igual en el próximo arranque en frío. No se miente diciendo
      // que se aplicó.
      console.warn('[actualizacion] no se pudo aplicar:', e?.message);
      setAplicando(false);
    }
  };

  return (
    <Animated.View
      style={[styles.barra, { transform: [{ translateY: deslizar }] }]}
      pointerEvents={seVe ? 'auto' : 'none'}
    >
      <Ionicons name="arrow-down-circle" size={16} color="#FFF" />
      <View style={{ flex: 1 }}>
        <Text style={styles.texto} numberOfLines={2}>
          {aplicando ? 'Aplicando la mejora…' : 'Hay una mejora lista para tu app'}
        </Text>
      </View>
      {!aplicando && (
        <TouchableOpacity
          onPress={aplicar}
          style={styles.boton}
          accessibilityRole="button"
          accessibilityLabel="Aplicar ahora la mejora descargada"
        >
          <Text style={styles.botonTexto}>Aplicar</Text>
        </TouchableOpacity>
      )}
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  // Mismas medidas que OfflineBanner para que las dos barras se lean como la
  // misma cosa y no como dos adornos distintos.
  barra: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 50 : 30,
    left: 16,
    right: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 22,
    backgroundColor: '#1565C0',
    zIndex: 9998, // justo por debajo del aviso de «sin conexión»
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowOffset: { width: 0, height: 3 },
    shadowRadius: 6,
    elevation: 6,
  },
  texto: { color: '#FFF', fontSize: 12.5, fontWeight: '600', lineHeight: 16 },
  boton: {
    backgroundColor: 'rgba(255,255,255,0.22)',
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 5,
  },
  botonTexto: { color: '#FFF', fontSize: 12.5, fontWeight: '800' },
});
